/* eslint sonarjs/no-duplicate-string: "off" */
import OperationalInfo from "@components/OperationalInfo/OperationalInfo";
import Navbar from "@components/client/Navbar";
import { pathToSlugs } from "@utils/slugHelper";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { Suspense } from "react";

import SectorGuide from "../sectorGuide";
import NavigationSlot from "./NavigationSlot";
import NavigationWrapper from "./NavigationWrapper";
import RenderMenu from "./RenderMenu";
import navigationStyles from "./navigation.module.css";
import { shouldHideDesktopNavigation } from "./navigationVisibility";

interface INavigationProps {
  toggleMenu?: boolean;
  setToggleMenu?: (v?: any) => void;
  layoutData: any;
  mobileMenuId?: string;
  is404: boolean;
}

type SlugAndPath = {
  slug: string;
  path: string;
};

export const shouldHideMobileMenu = (slug: string): boolean => {
  if (slug === "nya-startsidan") {
    return true;
  }

  const requiresOpenNavigation =
    slug === "start" || slug === "om-webbplatsen" || slug === "om-betasajten";

  return requiresOpenNavigation;
};

export const resolveSlugAndPath = (pathName: string, isTablet: boolean): SlugAndPath => {
  const cleanPath = pathName.replace(/\?.+/, "");
  const slug = pathName === "/" ? "start" : pathName;
  const path = cleanPath;

  if (path?.startsWith("/forsakringshandboken")) {
    return {
      slug: "forsakring",
      path: "/forsakring/utbildning-och-material/forsakringshandboken-redirect",
    };
  }

  if (path?.startsWith("/om-oss/nyheter/")) {
    return { slug: "om-oss", path: "/om-oss/nyheter" };
  }

  if (path?.startsWith("/om-oss/pressrum/")) {
    return { slug: "om-oss", path: "/om-oss/pressrum" };
  }

  if (path?.startsWith("/om-oss/evenemang/")) {
    return { slug: "om-oss", path: "/om-oss/evenemang" };
  }

  if (
    path?.startsWith("/ohalsa-och-arbetsskador/analys-och-statistik/aktuella-statistikrapporter/")
  ) {
    return {
      slug: "ohalsa-och-arbetsskador",
      path: "/ohalsa-och-arbetsskador/analys-och-statistik",
    };
  }

  if (path?.startsWith("/kundtjanst/fragor-och-svar/")) {
    return { slug: "kundtjanst", path: "/kundtjanst/fragor-och-svar" };
  }

  if (path?.startsWith("/anslutna-utbildningsanordnare")) {
    return {
      slug: "ohalsa-och-arbetsskador",
      path: "/ohalsa-och-arbetsskador/stod-for-arbetsmiljoutbildning",
    };
  }

  if (path?.startsWith("/varumarke") && !isTablet) {
    return { slug: "varumarke", path };
  }

  if (path?.startsWith("/intressant-vetande")) {
    return { slug: "intressant-vetande", path };
  }

  if (path?.startsWith("/ingen-missad-ersattning")) {
    return { slug: "ingen-missad-ersattning", path };
  }

  if (path?.startsWith("/tank-pa-oss")) {
    return { slug: "tank-pa-oss", path };
  }

  if (path?.startsWith("/filer")) {
    return { slug: "kundtjanst", path: "/kundtjanst/sok-filer-och-blanketter" };
  }

  return { slug, path };
};

const NavigationContent = ({ layoutData, mobileMenuId, is404 }: INavigationProps) => {
  const mobileMenuShortcuts = layoutData.siteHeaderData?.mobileMenuShortcuts;
  const menuItems = layoutData.siteHeaderData?.menuItems;

  return (
    <>
      <NavigationWrapper>
        <Suspense>
          <NuqsAdapter>
            <Navbar
              menu={
                <RenderMenu
                  menuItems={menuItems}
                  mobileMenuShortcuts={mobileMenuShortcuts}
                  mobileMenuId={mobileMenuId}
                  is404={is404}
                />
              }
            />

            <SectorGuide />
          </NuqsAdapter>
        </Suspense>
      </NavigationWrapper>

      <OperationalInfo />
    </>
  );
};

const Navigation = (props: INavigationProps) => {
  const initialPath = props.layoutData.navigationPath ?? "/";
  const hideDesktopNavigation = shouldHideDesktopNavigation(
    pathToSlugs(initialPath)[0],
    props.is404,
    initialPath,
  );

  return (
    <Suspense
      fallback={
        <div className={hideDesktopNavigation ? undefined : navigationStyles.navigationSlot} />
      }
    >
      <NavigationSlot initialPath={initialPath} is404={props.is404}>
        <Suspense fallback={null}>
          <NuqsAdapter>
            <NavigationContent {...props} />
          </NuqsAdapter>
        </Suspense>
      </NavigationSlot>
    </Suspense>
  );
};

export default Navigation;
